/**
 * EditDialog — the shared Dialog + form chrome used by every
 * section-card edit popover (Identity, Contact, Address, Tax,
 * Terms). Wraps Radix Dialog with a title/description/body/footer
 * layout and a Save/Cancel pair.
 *
 * Per-section dialogs just supply the form fields as children +
 * the onSubmit + pending state. Validation lives in the caller
 * (alongside payload assembly).
 *
 * Why this pattern over a generic field-driven dialog: each
 * section's fields have different types (state-select, rupees,
 * toggle, etc.) and slightly different validation. A schema-
 * driven dialog would balloon to handle them; the small bit of
 * caller boilerplate is cheaper than that complexity.
 */
import * as React from "react";
import { DialogRoot, DialogContent, DialogClose, cn } from "@flint/ui";

export interface EditDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  description?: React.ReactNode;
  pending: boolean;
  serverError: string | null;
  onSubmit: (e: React.FormEvent) => void;
  children: React.ReactNode;
  /**
   * Tailwind `max-w-*` class for the dialog shell. Defaults to `max-w-md`
   * (matches the simple section dialogs). Set to `max-w-lg`/`max-w-2xl`
   * for the rich Tax + Terms dialogs that pack more controls.
   */
  maxWidthClass?: string;
}

export function EditDialog({
  open,
  onOpenChange,
  title,
  description,
  pending,
  serverError,
  onSubmit,
  children,
  maxWidthClass = "max-w-md",
}: EditDialogProps) {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className={maxWidthClass}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <header>
            <h2 className="text-base font-semibold text-ink m-0">{title}</h2>
            {description ? (
              <p className="text-xs text-muted mt-1 leading-snug">{description}</p>
            ) : null}
          </header>
          {children}
          {serverError ? (
            <p className="text-xs text-bad font-medium" role="alert">
              {serverError}
            </p>
          ) : null}
          <footer className="flex items-center justify-end gap-2 pt-2 border-t border-line">
            <DialogClose asChild>
              <button type="button" className="h-9 px-3 rounded-md text-sm text-ink-2 hover:bg-bg">
                Cancel
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending}
              className={cn(
                "h-9 px-4 rounded-md bg-ink text-paper text-sm font-semibold",
                "hover:bg-ink-2 disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {pending ? "Saving…" : "Save"}
            </button>
          </footer>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
